import { promiseTry, LOCAL_FONT_PROTOCOL } from "@excalidraw/common";

import { subsetWoff2GlyphsByCodepoints } from "../subset/subset-main";

import { fontWarningSuppressor } from "./font-warning-suppressor";

type DataURL = string;

export class ExcalidrawFontFace {
  public readonly urls: URL[] | DataURL[];
  public readonly fontFace: FontFace;

  private static readonly ASSETS_FALLBACK_URL = `https://esm.sh/${
    import.meta.env.PKG_NAME
      ? `${import.meta.env.PKG_NAME}@${import.meta.env.PKG_VERSION}` // is provided during package build
      : "@excalidraw/excalidraw" // fallback to the latest package version (i.e. for app)
  }/dist/prod/`;

  constructor(family: string, uri: string, descriptors?: FontFaceDescriptors) {
    this.urls = ExcalidrawFontFace.createUrls(uri);

    // 过滤掉可能导致解码错误的无效URL
    const validUrls = (this.urls as (URL | DataURL)[]).filter(
      (url: URL | DataURL) => {
        const urlString = url.toString();
        // 检查data URL是否有效
        if (urlString.startsWith("data:")) {
          const dataUrlMatch = urlString.match(/data:[^,]*,(.*)/);
          if (dataUrlMatch && dataUrlMatch[1].trim() === "") {
            return false; // 跳过空的data URL
          }
        }
        return true;
      },
    );

    // 如果没有有效的URL，使用一个安全的fallback
    const urlsToUse = validUrls.length > 0 ? validUrls : this.urls;

    const sources = urlsToUse
      .map(
        (url: URL | DataURL) =>
          `url(${url}) ${ExcalidrawFontFace.getFormat(url)}`,
      )
      .join(", ");

    // 如果没有有效的源，创建一个最小的FontFace以避免错误
    const finalSources =
      sources || 'url(data:font/woff2;base64,) format("woff2")';

    try {
      this.fontFace = new FontFace(family, finalSources, {
        display: "swap",
        style: "normal",
        weight: "400",
        ...descriptors,
      });
    } catch (error) {
      // 如果FontFace构造失败，创建一个空的fallback FontFace
      // 这可以防止浏览器显示intervention警告
      this.fontFace = new FontFace(
        family,
        'url(data:font/woff2;base64,) format("woff2")',
        {
          display: "swap",
          style: "normal",
          weight: "400",
          ...descriptors,
        },
      );
    }
  }

  /**
   * Generates CSS `@font-face` definition with the (subsetted) font source as a data url for the characters within the unicode range.
   *
   * Retrieves `undefined` otherwise.
   */
  public toCSS(characters: string): Promise<string> | undefined {
    // quick exit in case the characters are not within this font face's unicode range
    if (!this.getUnicodeRangeRegex().test(characters)) {
      return;
    }

    const codepoints = Array.from(characters).map(
      (char) => char.codePointAt(0)!,
    );

    return this.getContent(codepoints)
      .then((content) => {
        // 检查内容是否有效，如果无效则返回空的CSS规则
        if (!content || content.length === 0) {
          return "";
        }
        return `@font-face { font-family: ${this.fontFace.family}; src: url(${content}); }`;
      })
      .catch(() => {
        // 静默处理字体CSS生成错误，返回空字符串而不是null
        return "";
      });
  }

  /**
   * Tries to fetch woff2 content, based on the registered urls (from first to last, treated as fallbacks).
   *
   * @returns base64 with subsetted glyphs based on the passed codepoint, last defined url otherwise
   */
  public async getContent(codePoints: Array<number>): Promise<string> {
    return fontWarningSuppressor.withSuppression(async () => {
      let i = 0;
      const errorMessages = [];

      while (i < this.urls.length) {
        const url = this.urls[i];

        try {
          const arrayBuffer = await this.fetchFont(url);

          // 检查是否获取到有效的字体数据
          if (arrayBuffer.byteLength === 0) {
            errorMessages.push(
              `"${url.toString()}" returned empty or invalid font data`,
            );
            i++;
            continue;
          }

          const base64 = await subsetWoff2GlyphsByCodepoints(
            arrayBuffer,
            codePoints,
          );

          // 检查子集化是否成功
          if (base64 && base64.length > 0) {
            return base64;
          }
          errorMessages.push(`"${url.toString()}" failed to subset font`);
        } catch (e) {
          // 静默处理错误，避免在控制台显示
          errorMessages.push(`"${url.toString()}" returned error "${e}"`);
        }

        i++;
      }

      // 完全静默处理字体加载失败，不在控制台显示任何错误信息
      // 这些错误通常是由于字体文件损坏、网络问题或CDN问题导致的
      // 应用程序会自动回退到系统字体，不影响用户体验

      // in case of issues, at least return the last url as a content
      // defaults to unpkg for bundled fonts (so that we don't have to host them forever) and http url for others
      return this.urls.length ? this.urls[this.urls.length - 1].toString() : "";
    });
  }

  public fetchFont(url: URL | DataURL): Promise<ArrayBuffer> {
    return fontWarningSuppressor.withSuppression(async () => {
      return promiseTry(async () => {
        const response = await fetch(url, {
          // always prefer cache (even stale), otherwise it always triggers an unnecessary validation request
          // which we don't need as we are controlling freshness of the fonts with the stable hash suffix in the url
          // https://developer.mozilla.org/en-US/docs/Web/API/Request/cache
          cache: "force-cache",
          headers: {
            Accept: "font/woff2",
          },
        });

        if (!response.ok) {
          // 静默处理错误，不抛出异常，避免控制台显示错误
          return new ArrayBuffer(0);
        }

        try {
          const arrayBuffer = await response.arrayBuffer();

          // 验证 WOFF2 文件格式的有效性
          // WOFF2 文件的前 4 个字节应该是 'wOF2'
          if (arrayBuffer.byteLength >= 4) {
            const signature = new Uint8Array(arrayBuffer, 0, 4);
            const expectedSignature = new Uint8Array([0x77, 0x4f, 0x46, 0x32]); // 'wOF2'

            // 检查签名是否匹配
            const isValidWoff2 = signature.every(
              (byte, index) => byte === expectedSignature[index],
            );

            if (!isValidWoff2) {
              // 不是有效的 WOFF2 文件，静默返回空 ArrayBuffer
              return new ArrayBuffer(0);
            }
          } else {
            // 文件太小，不可能是有效的 WOFF2 文件
            return new ArrayBuffer(0);
          }

          return arrayBuffer;
        } catch (decodeError) {
          // 捕获解码错误，静默返回空的 ArrayBuffer
          return new ArrayBuffer(0);
        }
      });
    });
  }

  private getUnicodeRangeRegex() {
    // using \u{h} or \u{hhhhh} to match any number of hex digits,
    // otherwise we would get an "Invalid Unicode escape" error
    // e.g. U+0-1007F -> \u{0}-\u{1007F}
    const unicodeRangeRegex = this.fontFace.unicodeRange
      .split(/,\s*/)
      .map((range) => {
        const [start, end] = range.replace("U+", "").split("-");
        if (end) {
          return `\\u{${start}}-\\u{${end}}`;
        }

        return `\\u{${start}}`;
      })
      .join("");

    return new RegExp(`[${unicodeRangeRegex}]`, "u");
  }

  private static createUrls(uri: string): URL[] | DataURL[] {
    if (uri.startsWith("data")) {
      // don't create the URL instance, as parsing the huge dataurl string is expensive
      return [uri];
    }

    if (uri.startsWith(LOCAL_FONT_PROTOCOL)) {
      // no url for local fonts
      return [];
    }

    if (uri.startsWith("http")) {
      // one url for http imports or data url
      return [new URL(uri)];
    }

    // absolute assets paths, which are found in tests and excalidraw-app build, won't work with base url, so we are stripping initial slash away
    const assetUrl: string = uri.replace(/^\/+/, "");
    const urls: URL[] = [];

    if (typeof window.EXCALIDRAW_ASSET_PATH === "string") {
      const normalizedBaseUrl = this.normalizeBaseUrl(
        window.EXCALIDRAW_ASSET_PATH,
      );

      urls.push(new URL(assetUrl, normalizedBaseUrl));

      // 如果设置了自定义资源路径，不添加CDN fallback以避免控制台警告
      // 这样可以防止浏览器在本地字体加载失败时尝试从CDN加载
      return urls;
    } else if (Array.isArray(window.EXCALIDRAW_ASSET_PATH)) {
      window.EXCALIDRAW_ASSET_PATH.forEach((path) => {
        const normalizedBaseUrl = this.normalizeBaseUrl(path);
        urls.push(new URL(assetUrl, normalizedBaseUrl));
      });

      // 如果设置了自定义资源路径数组，不添加CDN fallback以避免控制台警告
      return urls;
    }

    // 只有在没有设置EXCALIDRAW_ASSET_PATH时才使用CDN fallback
    urls.push(new URL(assetUrl, ExcalidrawFontFace.ASSETS_FALLBACK_URL));

    return urls;
  }

  private static getFormat(url: URL | DataURL) {
    if (!(url instanceof URL)) {
      // format is irrelevant for data url
      return "";
    }

    try {
      const parts = new URL(url).pathname.split(".");

      if (parts.length === 1) {
        return "";
      }

      return `format('${parts.pop()}')`;
    } catch (error) {
      return "";
    }
  }

  private static normalizeBaseUrl(baseUrl: string) {
    let result = baseUrl;

    // in case user passed a root-relative url (~absolute path),
    // like "/" or "/some/path", or relative (starts with "./"),
    // prepend it with `location.origin`
    if (/^\.?\//.test(result)) {
      result = new URL(
        result.replace(/^\.?\/+/, ""),
        window?.location?.origin,
      ).toString();
    }

    // ensure there is a trailing slash, otherwise url won't be correctly concatenated
    result = `${result.replace(/\/+$/, "")}/`;

    return result;
  }
}
